import React, { createContext, useContext, useReducer, useEffect } from "react";
import hierarchyService from "../services/hierarchyService";

// Estado inicial del contexto de jerarquía
const initialState = {
  hierarchyTree: [],
  selectedNode: null,
  breadcrumbPath: [],
  expandedNodes: new Set(),
  isLoading: false,
  error: null,
  searchResults: [],
  searchTerm: "",
  filters: {},
  cache: new Map(),
};

// Tipos de acciones
const HierarchyActionTypes = {
  LOAD_HIERARCHY_START: "LOAD_HIERARCHY_START",
  LOAD_HIERARCHY_SUCCESS: "LOAD_HIERARCHY_SUCCESS",
  LOAD_HIERARCHY_FAILURE: "LOAD_HIERARCHY_FAILURE",
  SELECT_NODE: "SELECT_NODE",
  SET_BREADCRUMB: "SET_BREADCRUMB",
  EXPAND_NODE: "EXPAND_NODE",
  COLLAPSE_NODE: "COLLAPSE_NODE",
  TOGGLE_NODE: "TOGGLE_NODE",
  SET_SEARCH_RESULTS: "SET_SEARCH_RESULTS",
  SET_SEARCH_TERM: "SET_SEARCH_TERM",
  SET_FILTERS: "SET_FILTERS",
  CLEAR_SEARCH: "CLEAR_SEARCH",
  UPDATE_CACHE: "UPDATE_CACHE",
  CLEAR_ERROR: "CLEAR_ERROR",
  REFRESH_HIERARCHY: "REFRESH_HIERARCHY",
};

// Reducer para manejo del estado de jerarquía
const hierarchyReducer = (state, action) => {
  switch (action.type) {
    case HierarchyActionTypes.LOAD_HIERARCHY_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case HierarchyActionTypes.LOAD_HIERARCHY_SUCCESS:
      return {
        ...state,
        hierarchyTree: action.payload.tree,
        isLoading: false,
        error: null,
      };

    case HierarchyActionTypes.LOAD_HIERARCHY_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      };

    case HierarchyActionTypes.SELECT_NODE:
      return {
        ...state,
        selectedNode: action.payload.node,
      };

    case HierarchyActionTypes.SET_BREADCRUMB:
      return {
        ...state,
        breadcrumbPath: action.payload.path,
      };

    case HierarchyActionTypes.EXPAND_NODE:
      return {
        ...state,
        expandedNodes: new Set([...state.expandedNodes, action.payload.nodeId]),
      };

    case HierarchyActionTypes.COLLAPSE_NODE:
      const newExpandedNodes = new Set(state.expandedNodes);
      newExpandedNodes.delete(action.payload.nodeId);
      return {
        ...state,
        expandedNodes: newExpandedNodes,
      };

    case HierarchyActionTypes.TOGGLE_NODE:
      const toggledNodes = new Set(state.expandedNodes);
      if (toggledNodes.has(action.payload.nodeId)) {
        toggledNodes.delete(action.payload.nodeId);
      } else {
        toggledNodes.add(action.payload.nodeId);
      }
      return {
        ...state,
        expandedNodes: toggledNodes,
      };

    case HierarchyActionTypes.SET_SEARCH_RESULTS:
      return {
        ...state,
        searchResults: action.payload.results,
      };

    case HierarchyActionTypes.SET_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload.term,
      };

    case HierarchyActionTypes.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload.filters },
      };

    case HierarchyActionTypes.CLEAR_SEARCH:
      return {
        ...state,
        searchResults: [],
        searchTerm: "",
      };

    case HierarchyActionTypes.UPDATE_CACHE:
      const newCache = new Map(state.cache);
      newCache.set(action.payload.key, {
        data: action.payload.data,
        timestamp: Date.now(),
      });
      return {
        ...state,
        cache: newCache,
      };

    case HierarchyActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case HierarchyActionTypes.REFRESH_HIERARCHY:
      return {
        ...state,
        cache: new Map(),
      };

    default:
      return state;
  }
};

// Crear el contexto
const HierarchyContext = createContext();

// Exportar el contexto como named export
export { HierarchyContext };

// Re-exportar el hook desde hooks/useHierarchy para compatibilidad
export { useHierarchy } from "../hooks/useHierarchy";

// Provider del contexto de jerarquía
export const HierarchyProvider = ({ children }) => {
  const [state, dispatch] = useReducer(hierarchyReducer, initialState);

  // Cargar jerarquía completa
  const loadHierarchy = async (force = false) => {
    // Verificar caché si no es forzado
    if (!force) {
      const cached = getCachedData("hierarchy");
      if (cached) {
        dispatch({
          type: HierarchyActionTypes.LOAD_HIERARCHY_SUCCESS,
          payload: { tree: cached },
        });
        return cached;
      }
    }

    dispatch({ type: HierarchyActionTypes.LOAD_HIERARCHY_START });

    try {
      const data = await hierarchyService.hierarchy.getFull();
      const tree = hierarchyService.utils.buildHierarchyTree(data.data || data);

      dispatch({
        type: HierarchyActionTypes.LOAD_HIERARCHY_SUCCESS,
        payload: { tree },
      });

      // Actualizar caché
      updateCache("hierarchy", tree);

      return tree;
    } catch (error) {
      dispatch({
        type: HierarchyActionTypes.LOAD_HIERARCHY_FAILURE,
        payload: { error: error.message },
      });
      throw error;
    }
  };

  // Seleccionar un nodo
  const selectNode = async (node) => {
    dispatch({
      type: HierarchyActionTypes.SELECT_NODE,
      payload: { node },
    });

    // Generar breadcrumb
    if (node) {
      const path = hierarchyService.utils.getPathToNode(state.hierarchyTree, node.id);
      if (path) {
        dispatch({
          type: HierarchyActionTypes.SET_BREADCRUMB,
          payload: { path },
        });
      }
    } else {
      dispatch({
        type: HierarchyActionTypes.SET_BREADCRUMB,
        payload: { path: [] },
      });
    }
  };

  // Expandir nodo
  const expandNode = (nodeId) => {
    dispatch({
      type: HierarchyActionTypes.EXPAND_NODE,
      payload: { nodeId },
    });
  };

  // Colapsar nodo
  const collapseNode = (nodeId) => {
    dispatch({
      type: HierarchyActionTypes.COLLAPSE_NODE,
      payload: { nodeId },
    });
  };

  // Alternar expansión de nodo
  const toggleNode = (nodeId) => {
    dispatch({
      type: HierarchyActionTypes.TOGGLE_NODE,
      payload: { nodeId },
    });
  };

  // Buscar en la jerarquía
  const search = async (searchTerm, filters = {}) => {
    if (!searchTerm.trim()) {
      dispatch({ type: HierarchyActionTypes.CLEAR_SEARCH });
      return [];
    }

    try {
      const results = await hierarchyService.search.global(searchTerm, filters);

      dispatch({
        type: HierarchyActionTypes.SET_SEARCH_RESULTS,
        payload: { results: results.data || results },
      });

      dispatch({
        type: HierarchyActionTypes.SET_SEARCH_TERM,
        payload: { term: searchTerm },
      });

      return results.data || results;
    } catch (error) {
      console.error("Error en búsqueda:", error);
      throw error;
    }
  };

  // Limpiar búsqueda
  const clearSearch = () => {
    dispatch({ type: HierarchyActionTypes.CLEAR_SEARCH });
  };

  // Establecer filtros
  const setFilters = (filters) => {
    dispatch({
      type: HierarchyActionTypes.SET_FILTERS,
      payload: { filters },
    });
  };

  // Navegar hasta un nodo específico
  const navigateToNode = async (nodeId) => {
    const node = hierarchyService.utils.findNodeById(state.hierarchyTree, nodeId);
    if (node) {
      await selectNode(node);

      // Expandir todos los nodos padre
      const path = hierarchyService.utils.getPathToNode(state.hierarchyTree, nodeId);
      if (path) {
        path.forEach((pathNode, index) => {
          if (index < path.length - 1) {
            // No expandir el nodo final
            expandNode(pathNode.id);
          }
        });
      }
    }
  };

  // Obtener estadísticas de la jerarquía
  const getStats = () => {
    return hierarchyService.utils.getHierarchyStats(state.hierarchyTree);
  };

  // Funciones de caché
  const getCachedData = (key, maxAge = 600000) => {
    // 10 minutos por defecto
    const cached = state.cache.get(key);
    if (cached && Date.now() - cached.timestamp < maxAge) {
      return cached.data;
    }
    return null;
  };

  const updateCache = (key, data) => {
    dispatch({
      type: HierarchyActionTypes.UPDATE_CACHE,
      payload: { key, data },
    });
  };

  // Refrescar jerarquía
  const refreshHierarchy = async () => {
    dispatch({ type: HierarchyActionTypes.REFRESH_HIERARCHY });
    return await loadHierarchy(true);
  };

  // Limpiar error
  const clearError = () => {
    dispatch({ type: HierarchyActionTypes.CLEAR_ERROR });
  };

  // Cargar jerarquía al inicializar
  useEffect(() => {
    loadHierarchy();
  }, []);

  // Valor del contexto
  const contextValue = {
    // Estado y dispatch para hooks personalizados
    state,
    dispatch,

    // Estado expandido para acceso directo
    ...state,

    // Acciones principales
    loadHierarchy,
    refreshHierarchy,
    selectNode,
    navigateToNode,

    // Navegación de árbol
    expandNode,
    collapseNode,
    toggleNode,

    // Búsqueda
    search,
    clearSearch,
    setFilters,

    // Utilidades
    getStats,
    clearError,
  };

  return <HierarchyContext.Provider value={contextValue}>{children}</HierarchyContext.Provider>;
};

export default HierarchyContext;
